import React from "react";
import {
  Form,
  Input,
  Button,
  Card,
  AutoComplete,
  List,
  Popconfirm,
  notification,
  Modal,
  Checkbox,
  Space,
  Alert,
  Tag,
  Tour,
  Spin,
  InputNumber,
  Divider,
  Select,
} from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { apiCallPost, apiCallPostCancellable, isBelow40 } from "../fns/util";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";

const terms = [
  "Chief instructor is solely responsible for giving the consent for participation and declaration on behalf of all the co-instructors including himself. Rules and guidelines regarding defaulter is applicable as per the general guidelines of abstract submission of OSWB.",
  "OSWB reserves the right of rejection or acceptance (in parts / whole) with necessary modifications in the structure of IC in consultation with the Chief Instructor.",
  "The Scientific Committee of OSWB will have the discretion to rearrange or shift submissions in the best interest of the scientific program of the conference.",
  "Failure to present the course at the conference without prior intimation with valid reasons will automatically disqualify the chief instructor from submitting any work in the subsequent conference.",
  "All instructors will have to register as delegates for the conference.",
  "You are not allowed to change the chief instructor except with prior permission and valid reasons.",
  "You have not submitted this work to another conference, venue or journal for consideration.",
];

class ICSubmissionForm extends React.Component {
  // Create references for form fields
  refTitle = React.createRef();
  refIntroduction = React.createRef();
  refAuthors = React.createRef();
  refAddAuthor = React.createRef();
  refAwardTrack = React.createRef();
  refTerms = React.createRef();
  refDraft = React.createRef();
  tourSteps = [
    {
      title: "Enter your course title",
      description: "You can enter the title of your course here.",
      target: () => this.refTitle.current && this.refTitle.current.input,
    },
    {
      title: "Enter a short synopsis of your course.",
      description:
        "Enter a short synopsis of your course and associated level in this section. This will be visible to the scientific committee, and will help them understand what your course is about.",
      target: () =>
        this.refIntroduction.current &&
        this.refIntroduction.current.resizableTextArea.textArea,
    },
    {
      title: "Add instructors.",
      description:
        "You should add between 3 to 6 instructors for your course. For each instructor, provide the topic, a brief of the topic, and the duration of the topic. The total duration of the course should not exceed 45 minutes.",
      target: () => this.refAuthors.current,
    },
    {
      title: "Add outside collaborators",
      description:
        "If your co-instructor is not a member of OSWB, you can enter their name, phone and email, and add them. Remember that per the rules of the conference, only a member can be a chief instructor.",
      target: () => this.refAddAuthor.current,
    },
    {
      title: "Read the rules",
      description:
        "Before you submit, make sure you've read the rules. The decision of the scientific committee will be final for all submissions, and if you have any queries, please reach out to us before you submit.",
      target: () => this.refTerms.current && this.refTerms.current.input,
    },
    {
      title: "Save your work",
      description:
        "At any point, if you want, you can save the current state of your submission as a draft. Note that authors added will not be saved in the draft; only your abstract is saved. Once you're ready and you've filled in everything, you can go ahead and submit. Good luck!",
      target: () => this.refDraft.current,
    },
  ];
  colorMap = {
    DRAFT: "orange",
    SUBMITTED: "blue",
    WITHDRAWN: "red",
  };
  // Initialize state
  state = {
    isTourOpen: false,
    termsAgreed: false,
    searchResults: [],
    isSaving: false,
    newUser: { name: "", email: "", phone: "", role: "" },
    title: "",
    content: {
      description: "",
      level: "",
      topics: [],
    },
    lifecycle: "DRAFT",
    duration: 5,
    awardTrack: "",
    authors: [],
    presentingAuthorId: "",
    presentingAuthorName: "",
  };
  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
    this.setState({
      lifecycle: this.props.preloadedData.lifecycle,
      title: this.props.preloadedData.title,
      content: this.props.preloadedData.content
        ? this.props.preloadedData.content
        : this.state.content,
      duration: 45,
      awardTrack: this.props.preloadedData.awardTrack,
      authors: this.props.preloadedData.authors,
      presentingAuthorId:
        this.props.preloadedData.presentingAuthorId || undefined,
      presentingAuthorName:
        this.props.preloadedData.presentingAuthorName || undefined,
    });
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }
  handleResize = () => {
    this.setState({ isDesktop: window.innerWidth >= 1000 });
  };

  handleSearch = async (searchText) => {
    if (!searchText || (searchText && searchText.length <= 2)) {
      return;
    }
    let token = await this.props.context.provideToken();
    try {
      this.setState({ isSearching: true });
      let res = await apiCallPostCancellable(
        token,
        "/conference2023/searchForParticipants",
        { prefix: searchText }
      );
      if (res) {
        this.setState({
          searchResults: res,
          isSearching: false,
        });
      }
    } catch (err) {
      this.setState({ isSearching: false });
    }
  };
  handleAddAuthorFromSearch = async (delegate) => {
    if (this.state.authors.length === 6) {
      notification.error({
        message: "Cannot add more than 6 instructors.",
      });
    }
    let authors = this.state.authors;
    if (authors.find((x) => x.delegateId === delegate._id)) {
      notification.error({
        message: "This instructor is already added.",
        description: "Please select another instructor.",
      });
      return;
    }
    if (delegate.searchresultsrc === "SRC_CONF") {
      let content = this.state.content;
      let topics = content.topics;
      topics.push({
        topic: "",
        duration: 5,
        abstract: "",
        instructorId: delegate._id,
      });
      content.topics = topics;
      this.setState({
        authors: [
          ...this.state.authors,
          {
            delegateId: delegate._id,
            name: delegate.full_name,
            email: delegate.email,
            mobile: delegate.mobile_number,
            isAgeBelow40: delegate.isAgeBelow40,
            role: "Instructor",
          },
        ],
        content: content,
        searchResults: [],
      });
      return;
    }
    if (delegate.searchresultsrc === "SRC_OSWB") {
      let token = await this.props.context.provideToken();
      try {
        let res = await apiCallPost(token, "/conference2023/inviteDelegate", {
          name: delegate.full_name,
          email: delegate.email,
          mobile: delegate.mobile_number
            ? "+91" + delegate.mobile_number
            : null,
        });
        let content = this.state.content;
        let topics = content.topics;
        topics.push({
          topic: "",
          duration: 5,
          abstract: "",
          instructorId: res.delegate._id,
        });
        this.setState({
          authors: [
            ...this.state.authors,
            {
              delegateId: res.delegate._id,
              name: res.delegate.name,
              email: res.delegate.email,
              mobile: res.delegate.mobile,
              isAgeBelow40: res.delegate.isAgeBelow40,
              role: "Instructor",
            },
          ],
          content: content,
        });
      } catch (err) {
        notification.error({
          message: "Error adding instructor.",
        });
      }
    }
  };
  handleAddUser = async () => {
    if (this.state.authors.length === 6) {
      notification.error({
        message: "Cannot add more than 6 instructors.",
      });
    }
    this.setState({ showAddUserModal: false, isSaving: true });
    let token = await this.props.context.provideToken();
    try {
      let res = await apiCallPost(token, "/conference2023/inviteDelegate", {
        name: this.state.newUser.name,
        email: this.state.newUser.email,
        mobile: this.state.newUser.phone,
      });
      let content = this.state.content;
      let topics = content.topics;
      topics.push({
        topic: "",
        duration: 5,
        abstract: "",
        instructorId: res.delegate._id,
      });
      this.setState({
        isSaving: false,
        authors: [
          ...this.state.authors,
          {
            delegateId: res.delegate._id,
            name: res.delegate.name,
            email: res.delegate.email,
            mobile: res.delegate.mobile,
            isAgeBelow40: res.delegate.isAgeBelow40,
            role: "Instructor",
          },
        ],
        content: content,
      });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error adding instructor.",
      });
    }
  };
  handleDeleteUser = (user) => {
    let authors = this.state.authors;
    let topics = this.state.content.topics;
    let idx = _.findIndex(authors, (x) => x.delegateId === user.delegateId);
    authors.splice(idx, 1);
    topics = topics.filter((x) => x.instructorId !== user.delegateId);
    if (user.delegateId === this.state.presentingAuthorId) {
      this.setState({
        presentingAuthorId: "",
        presentingAuthorName: "",
      });
    }
    this.setState({
      authors: authors,
      content: {
        ...this.state.content,
        topics: topics,
      },
    });
  };
  handleMakePresentingAuthor = (e) => {
    let authors = this.state.authors;
    let idx = _.findIndex(authors, (x) => x.delegateId === e.delegateId);
    authors[idx].role = "Chief Instructor";

    authors.forEach((x) => {
      if (x.delegateId !== e.delegateId) {
        x.role = "Instructor";
      }
    });
    this.setState({
      presentingAuthorId: e.delegateId,
      authors: authors,
      presentingAuthorName: e.name,
    });
  };
  handleSubmit = async () => {
    let token = await this.props.context.provideToken();
    let totalDuration = 0;
    this.state.content.topics.forEach((x) => {
      totalDuration += x.duration;
    });
    try {
      this.setState({ isSaving: true });
      await apiCallPost(token, "/conference2023/editSubmission", {
        submissionId: this.props.preloadedData._id,
        submission: {
          title: this.state.title,
          content: this.state.content,
          awardTrack: this.state.awardTrack,
          authors: this.state.authors,
          presentingAuthorId: this.state.presentingAuthorId,
          presentingAuthorName: this.state.presentingAuthorName,
          duration: totalDuration,
          lifecycle: "SUBMITTED",
        },
      });
      this.setState({ isSaving: false, lifecycle: "SUBMITTED" });
      notification.success({
        message: "Submitted!",
      });
      await apiCallPost(token, "/conference2023/notifyOnSubmit", {
        submissionId: this.props.preloadedData._id,
      });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error creating submission.",
      });
    }
  };
  handleSaveDraft = async () => {
    let token = await this.props.context.provideToken();
    let totalDuration = 0;
    this.state.content.topics.forEach((x) => {
      totalDuration += x.duration;
    });
    try {
      this.setState({ isSaving: true });
      await apiCallPost(token, "/conference2023/editSubmission", {
        submissionId: this.props.preloadedData._id,
        submission: {
          title: this.state.title,
          content: this.state.content,
          awardTrack: this.state.awardTrack,
          authors: this.state.authors,
          presentingAuthorId: this.state.presentingAuthorId,
          presentingAuthorName: this.state.presentingAuthorName,
          duration: totalDuration,
          lifecycle: "DRAFT",
        },
      });
      this.setState({ isSaving: false });
      notification.success({
        message: "Draft saved.",
      });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error saving draft.",
      });
    }
  };
  renderSubmitButton = () => {
    if (this.state.lifecycle === "SUBMITTED") {
      return null;
      // add withdrawn later.
    }
    if (!this.state.authors.length) {
      return <Alert message="Please add instructors." type="error" showIcon />;
    }
    if (this.state.authors.length < 3) {
      return (
        <Alert
          message="Cannot have less than 3 instructors."
          type="error"
          showIcon
        />
      );
    }
    if (this.state.authors.length > 6) {
      return (
        <Alert
          message="Cannot have more than 6 instructors."
          type="error"
          showIcon
        />
      );
    }
    if (!this.state.presentingAuthorId) {
      return (
        <Alert message="No chief instructor selected." type="error" showIcon />
      );
    }
    if (
      !this.state.title ||
      !this.state.content.description ||
      !this.state.content.level
    ) {
      return (
        <Alert message="All fields must be filled." type="error" showIcon />
      );
    }
    let flagAllFilled = false;
    this.state.content.topics.forEach((x) => {
      if (x.topic && x.duration) {
        flagAllFilled = true;
      } else {
        flagAllFilled = false;
      }
    });
    if (!flagAllFilled) {
      return (
        <Alert
          message="Topics and durations must be filled for all authors."
          type="error"
          showIcon
        />
      );
    }

    // if total duration > 45 mins, not allowed
    let totalDuration = 0;
    this.state.content.topics.forEach((x) => {
      totalDuration += x.duration;
    });
    if (totalDuration > 45) {
      return (
        <Alert
          message="Total duration of course cannot be more than 45 mins."
          type="error"
          showIcon
        />
      );
    }

    if (!this.state.termsAgreed) {
      return (
        <Alert message="You must agree to the rules." type="error" showIcon />
      );
    }
    return (
      <Button
        disabled={this.state.isSaving}
        loading={this.state.isSaving}
        type="primary"
        htmlType="submit"
        icon={<CheckCircleOutlined />}
        style={{ backgroundColor: "green", borderColor: "green" }}
      >
        Submit
      </Button>
    );
  };
  renderBestViewedOnDesktopTag = () => {
    if (!this.state.isDesktop) {
      return (
        <Tag color="red">
          This page is best viewed on a desktop or laptop, or in landscape
          orientation.
        </Tag>
      );
    }
  };
  renderTopicSection = () => {
    return (
      <Card
        style={{
          boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
          margin: "auto",
          maxWidth: "800px",
        }}
      ></Card>
    );
  };
  render() {
    let topics = this.state.content.topics;
    let totalDuration = 0;
    topics.forEach((x) => {
      totalDuration += x.duration;
    });
    let durationColor = "green";
    if (totalDuration > 35) {
      durationColor = "orange";
    }
    if (totalDuration > 45) {
      durationColor = "red";
    }
    return (
      <Card
        style={{
          padding: "10px",
          margin: "auto",
          maxWidth: "800px",
        }}
        hoverable
      >
        <Space direction="vertical" size="middle">
          <h3 style={{ marginBottom: "20px" }}>
            Instruction Course Submission{" "}
            <Tag color={this.colorMap[this.state.lifecycle]}>
              {_.startCase(_.lowerCase(this.state.lifecycle))}
            </Tag>
          </h3>
          <Tour
            open={this.state.isTourOpen}
            onClose={() => this.setState({ isTourOpen: false })}
            steps={this.tourSteps}
          />
          {this.state.lifecycle === "DRAFT" && (
            <Button
              size="small"
              style={{
                backgroundColor: "#72CA9B",
                borderColor: "#72CA9B",
              }}
              icon={<QuestionCircleOutlined />}
              onClick={() => {
                this.setState({ isTourOpen: true });
              }}
            >
              Quick Tutorial
            </Button>
          )}
          {this.renderBestViewedOnDesktopTag()}

          <Form
            onFinish={this.handleSubmit}
            disabled={
              ["WITHDRAWN", "SUBMITTED"].includes(this.state.lifecycle) ||
              this.state.isSaving
            }
          >
            <Space direction="vertical" size="middle">
              {this.state.lifecycle === "DRAFT" ? (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <Tag color="blue">Overview of Course</Tag>
                  <p className="small_text">
                    You can enter a top-level description of your proposed
                    course here.
                  </p>

                  <Form.Item
                    label="Title"
                    labelCol={{ span: 24 }}
                    rules={[
                      { required: true, message: "Please input the title!" },
                    ]}
                    help="Enter the title of your course."
                  >
                    <Input
                      status={
                        this.state.title
                          ? this.state.title.length > 140
                            ? "error"
                            : this.state.title.length > 120
                            ? "warning"
                            : ""
                          : ""
                      }
                      ref={this.refTitle}
                      name="title"
                      value={this.state.title}
                      onChange={(e) =>
                        this.setState({
                          title: e.target.value,
                        })
                      }
                      maxLength={150}
                      showCount
                    />
                  </Form.Item>
                  <Form.Item
                    label="Synopsis"
                    labelCol={{ span: 24 }}
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter a short synopsis of your course.",
                      },
                    ]}
                    help="Provide a short synopsis of your course."
                  >
                    <Input.TextArea
                      status={
                        this.state.content.description
                          ? this.state.content.description.length > 495
                            ? "error"
                            : this.state.content.description.length > 400
                            ? "warning"
                            : ""
                          : ""
                      }
                      ref={this.refIntroduction}
                      name="introduction"
                      value={this.state.content.description}
                      onChange={(e) => {
                        let content = this.state.content;
                        content.description = e.target.value;
                        this.setState({ content: content });
                      }}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Level"
                    labelCol={{ span: 24 }}
                    rules={[
                      {
                        required: true,
                        message:
                          "Please provide the difficulty level of your course.",
                      },
                    ]}
                    help="Level of your course - Basic, Intermediate, Advanced."
                  >
                    <Select
                      onChange={(e) => {
                        let content = this.state.content;
                        content.level = e;
                        this.setState({ content: content });
                      }}
                      allowClear
                      value={this.state.content.level}
                    >
                      <Select.Option value="Basic">Basic</Select.Option>
                      <Select.Option value="Intermediate">
                        Intermediate
                      </Select.Option>
                      <Select.Option value="Advanced">Advanced</Select.Option>
                    </Select>
                  </Form.Item>
                </Card>
              ) : (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <h4>{this.state.title}</h4>
                  <p>
                    <b>Synopsis</b>: {this.state.content.description}
                  </p>
                  <p>
                    <b>Level</b>: {this.state.content.level}
                  </p>
                </Card>
              )}
              {this.state.lifecycle === "DRAFT" ? (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <Tag color="blue">Authors</Tag>
                  <p className="small_text">
                    Each course must have between 3 to 6 instructors.
                  </p>
                  <h4>
                    <Tag color={durationColor}>
                      Total Time: {totalDuration} mins
                    </Tag>
                  </h4>
                  <div>
                    <Form.Item label="Authors" labelCol={{ span: 24 }}>
                      <div ref={this.refAuthors}>
                        {" "}
                        <AutoComplete
                          disabled={this.state.authors.length === 5}
                          popupMatchSelectWidth={true}
                          style={{ width: 200 }}
                          options={this.state.searchResults.map((x) => ({
                            label: (
                              <Space direction="vertical" size="small">
                                <Tag
                                  color={
                                    x.searchresultsrc === "SRC_OSWB"
                                      ? "magenta"
                                      : "blue"
                                  }
                                >
                                  {x.full_name}
                                </Tag>
                                {x.membership_id && (
                                  <Tag>{x.membership_id}</Tag>
                                )}
                              </Space>
                            ),
                            value: `${x.full_name} - ${x.membership_id}`,
                            delegate: x,
                          }))}
                          onSearch={this.handleSearch}
                          placeholder="Search for an instructor"
                          onSelect={(value, option) => {
                            this.handleAddAuthorFromSearch(option.delegate);
                          }}
                        />
                        {this.state.isSearching && <Spin />}{" "}
                        <Button
                          disabled={this.state.authors.length === 6}
                          type="default"
                          onClick={() => {
                            let dd = this.props.loggedInUser;
                            if (this.state.authors.length === 6) {
                              notification.error({
                                message: "Cannot add more than 6 instructors.",
                              });
                            }
                            let authors = this.state.authors;
                            if (authors.find((x) => x.delegateId === dd._id)) {
                              notification.error({
                                message: "This instructor is already added.",
                                description:
                                  "Please select another instructor.",
                              });
                              return;
                            }
                            let content = this.state.content;
                            let topics = content.topics;
                            topics.push({
                              topic: "",
                              duration: 5,
                              abstract: "",
                              instructorId: dd._id,
                            });
                            this.setState({
                              content: content,
                              authors: [
                                ...this.state.authors,
                                {
                                  delegateId: dd._id,
                                  name: dd.name,
                                  email: dd.email,
                                  mobile: dd.mobile,
                                  isAgeBelow40: dd.dob
                                    ? isBelow40(dd.dob)
                                    : false,
                                  role: "Instructor",
                                },
                              ],
                            });
                          }}
                        >
                          Add Myself
                        </Button>
                        <Button
                          disabled={this.state.authors.length === 5}
                          ref={this.refAddAuthor}
                          type="default"
                          onClick={() =>
                            this.setState({ showAddUserModal: true })
                          }
                        >
                          Click to add a non-OSWB instructor
                        </Button>
                      </div>
                      <Modal
                        title="Add New Instructor"
                        visible={this.state.showAddUserModal}
                        onCancel={() =>
                          this.setState({ showAddUserModal: false })
                        }
                        footer={[
                          <Button
                            key="back"
                            onClick={() =>
                              this.setState({ showAddUserModal: false })
                            }
                          >
                            Cancel
                          </Button>,
                          <Button
                            key="submit"
                            type="primary"
                            loading={this.state.loading}
                            onClick={this.handleAddUser}
                            disabled={
                              !this.state.newUser.name ||
                              !this.state.newUser.email ||
                              !this.state.newUser.phone
                            }
                          >
                            Add
                          </Button>,
                        ]}
                      >
                        <Form>
                          <Form.Item
                            label="Name"
                            rules={[
                              {
                                required: true,
                                message: "Please input the name!",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Name"
                              value={this.state.newUser.name}
                              onChange={(e) =>
                                this.setState({
                                  newUser: {
                                    ...this.state.newUser,
                                    name: e.target.value,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            label="Email"
                            rules={[
                              {
                                required: true,
                                message: "Please input the email!",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Email"
                              value={this.state.newUser.email}
                              onChange={(e) =>
                                this.setState({
                                  newUser: {
                                    ...this.state.newUser,
                                    email: e.target.value,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            label="Phone"
                            rules={[
                              {
                                required: true,
                                message: "Please input the phone number!",
                              },
                            ]}
                          >
                            <PhoneInput
                              name="Phone"
                              defaultCountry="IN"
                              placeholder="Phone"
                              value={this.state.newUser.phone}
                              onChange={(e) =>
                                this.setState({
                                  newUser: {
                                    ...this.state.newUser,
                                    phone: e,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Form>
                      </Modal>
                      <Divider />
                      <br />
                      <List
                        locale={{ emptyText: "No instructors added." }}
                        itemLayout="horizontal"
                        dataSource={this.state.authors}
                        renderItem={(item) => {
                          let author = item;
                          let content = this.state.content;
                          let topics = content.topics;
                          let topicIdx = _.findIndex(
                            topics,
                            (x) => x.instructorId === author.delegateId
                          );
                          return (
                            <List.Item
                              style={{
                                marginBottom: "10px",
                              }}
                              actions={[]}
                            >
                              <List.Item.Meta
                                avatar={<UserOutlined />}
                                title={item.name}
                                description={item.role}
                              />{" "}
                              <Card
                                style={{
                                  marginTop: "10px",
                                }}
                              >
                                <Form>
                                  <Space direction="vertical" size="small">
                                    <Form.Item
                                      label="Topic"
                                      labelCol={{ span: 24 }}
                                      rules={[
                                        {
                                          required: true,
                                          message: "Please input the topic!",
                                        },
                                      ]}
                                      help="Topic of this section of the course"
                                    >
                                      <Input
                                        status={
                                          this.state.content.topics[topicIdx]
                                            .topic
                                            ? this.state.content.topics[
                                                topicIdx
                                              ].topic.length > 120
                                              ? "error"
                                              : this.state.content.topics[
                                                  topicIdx
                                                ].topic.length > 100
                                              ? "warning"
                                              : ""
                                            : ""
                                        }
                                        name="topic"
                                        value={
                                          this.state.content.topics[topicIdx]
                                            .topic
                                        }
                                        onChange={(e) => {
                                          let content = this.state.content;
                                          let topics = content.topics;
                                          topics[topicIdx].topic =
                                            e.target.value;
                                          content.topics = topics;
                                          this.setState({ content: content });
                                        }}
                                        maxLength={125}
                                        showCount
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Topic Brief"
                                      labelCol={{ span: 24 }}
                                      rules={[
                                        {
                                          required: true,
                                          message:
                                            "Please enter the topic abstract",
                                        },
                                      ]}
                                      help="A brief  for this topic."
                                      validateStatus={
                                        this.state.conclusionStatus
                                      }
                                    >
                                      <Input.TextArea
                                        status={
                                          this.state.content.topics[topicIdx]
                                            .abstract
                                            ? this.state.content.topics[
                                                topicIdx
                                              ].abstract.length > 400
                                              ? "error"
                                              : this.state.content.topics[
                                                  topicIdx
                                                ].abstract.length > 495
                                              ? "warning"
                                              : ""
                                            : ""
                                        }
                                        name="abstract"
                                        value={
                                          this.state.content.topics[topicIdx]
                                            .abstract
                                        }
                                        onChange={(e) => {
                                          let content = this.state.content;
                                          let topics = content.topics;
                                          topics[topicIdx].abstract =
                                            e.target.value;
                                          content.topics = topics;
                                          this.setState({ content: content });
                                        }}
                                        showCount
                                        maxLength={500}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Duration"
                                      labelCol={{ span: 24 }}
                                      rules={[
                                        {
                                          required: true,
                                          message:
                                            "Please enter the duration of this topic.",
                                        },
                                      ]}
                                      help="Duration for this topic, in minutes"
                                    >
                                      <InputNumber
                                        name="duration"
                                        min={5}
                                        max={45}
                                        value={
                                          this.state.content.topics[topicIdx]
                                            .duration
                                        }
                                        onChange={(e) => {
                                          let content = this.state.content;
                                          let topics = content.topics;
                                          topics[topicIdx].duration = e;
                                          content.topics = topics;
                                          this.setState({ content: content });
                                        }}
                                      />
                                    </Form.Item>
                                  </Space>
                                </Form>
                                <Space
                                  direction="horizontal"
                                  style={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Popconfirm
                                    title="Make this instructor the chief instructor? Note that you cannot change this after submission."
                                    onConfirm={() =>
                                      this.handleMakePresentingAuthor(item)
                                    }
                                  >
                                    <Button
                                      disabled={
                                        this.state.presentingAuthorId ===
                                        item.delegateId
                                      }
                                    >
                                      Make Chief Instructor
                                    </Button>
                                  </Popconfirm>
                                  <Popconfirm
                                    title="Sure you want to delete?"
                                    onConfirm={() =>
                                      this.handleDeleteUser(item)
                                    }
                                  >
                                    <Button danger>Delete</Button>
                                  </Popconfirm>
                                </Space>
                              </Card>
                            </List.Item>
                          );
                        }}
                      />
                    </Form.Item>
                  </div>
                </Card>
              ) : (
                <>
                  <Card
                    style={{
                      boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                      width: "100%",
                    }}
                  >
                    <List
                      locale={{ emptyText: "No instructors added." }}
                      itemLayout="vertical"
                      dataSource={this.state.authors}
                      renderItem={(item) => {
                        let author = item;
                        let content = this.state.content;
                        let topics = content.topics;
                        let topicIdx = _.findIndex(
                          topics,
                          (x) => x.instructorId === author.delegateId
                        );
                        return (
                          <List.Item>
                            <List.Item.Meta
                              avatar={<UserOutlined />}
                              title={item.name}
                              description={item.role}
                            />
                            <div style={{ width: "100%" }}>
                              <p>
                                Topic:{" "}
                                {this.state.content.topics[topicIdx].topic} |
                                Duration:{" "}
                                {this.state.content.topics[topicIdx].duration}{" "}
                                mins |{" "}
                                {this.state.content.topics[topicIdx].abstract
                                  .split("\n")
                                  .map((ee) => (
                                    <p>{ee}</p>
                                  ))}
                              </p>
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  </Card>
                </>
              )}

              {this.state.lifecycle === "DRAFT" && (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <Tag color="blue">Finalize submission</Tag>
                  <br />
                  <p className="small_text">
                    Ensure that you've read the rules for this submission:{" "}
                  </p>
                  <Form.Item>
                    <Form.Item>
                      <List
                        dataSource={terms}
                        renderItem={(item) => <List.Item>{item}</List.Item>}
                      />
                      <Checkbox
                        ref={this.refTerms}
                        onChange={(e) =>
                          this.setState({ termsAgreed: e.target.checked })
                        }
                      >
                        I have read and understood these rules.
                      </Checkbox>
                    </Form.Item>
                    <br />
                    {this.renderSubmitButton()}
                    {this.state.lifecycle === "DRAFT" && (
                      <Button
                        onClick={this.handleSaveDraft}
                        ref={this.refDraft}
                      >
                        Save as Draft
                      </Button>
                    )}
                  </Form.Item>
                </Card>
              )}
            </Space>
          </Form>
        </Space>
      </Card>
    );
  }
}

export default ICSubmissionForm;
